import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Text,
  useDisclosure,
  VStack,
  Divider,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Skeleton,
} from '@chakra-ui/react';
import React, { useRef } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { PermissionGuard } from '~/core/PermissionGuard';
import { colors } from '~/packages/common-frontend/colors';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import {
  TransactionsFilterState,
  useDeriveTransactionsFiltersFromBackend,
} from '~/packages/transactions/hooks/transactionsHooks';

// styles
const dropdownButtonProps = {
  as: Button,
  minWidth: '10rem',
  variant: 'secondary-muted',
  fontWeight: 400,
  width: '100%',
};
const menuListProps = {
  bgColor: colors.background[100],
  py: '0',
};
const inputProps = {
  backgroundColor: 'white',
};
const menuItemOptionProps = {
  bgColor: colors.background[100],
  _hover: { bg: colors.background[500] },
};

type TransactionsFiltersDrawerProps = TransactionsFilterState & {
  onFilterChange: (nextFilters: TransactionsFilterState) => void;
};
export const TransactionsFiltersDrawer = ({
  onFilterChange,
  ...initialFilter
}: TransactionsFiltersDrawerProps) => {
  const drawerControls = useDisclosure();
  const { facilities } = useCreditFacilitiesOrThrow();

  // Filters
  const result = useDeriveTransactionsFiltersFromBackend({
    creditFacilityIds: initialFilter.creditFacilityIds,
  });

  const filtersBtnRef = useRef(null);

  if (result.status === 'error') throw result.error;
  if (result.status === 'pending') return <Skeleton h="600px" />;

  return (
    <>
      <Button
        variant="secondary"
        colorScheme="v2-aurora"
        ref={filtersBtnRef}
        onClick={drawerControls.onOpen}
        gap="0.5rem"
      >
        <Icon icon="filter" />
        Filters
      </Button>
      <Drawer
        placement="right"
        finalFocusRef={filtersBtnRef}
        size="xl"
        {...drawerControls}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
            <Heading>Filters</Heading>
          </DrawerHeader>
          <Divider my="1.5rem" />
          <DrawerBody px="3rem">
            <VStack align="flex-start">
              <FormControl>
                <FormLabel>
                  <Heading fontSize="xl">Transaction Dates</Heading>
                  <Text color="gray">
                    Select a date range to apply the filter.
                  </Text>
                </FormLabel>
                <FullDateRangePicker // TODO: @haghanim add capability for customer to type date + fix border
                  onSelectDateRange={(newDateRange) => {
                    const [newStartDate, newEndDate] = newDateRange;
                    if (newStartDate && newEndDate) {
                      result.setCurrentFilters({
                        startDate: commonDate.dateToFullDateString(
                          newStartDate,
                        ),
                        endDate: commonDate.dateToFullDateString(newEndDate),
                      });
                    }
                  }}
                  currentDateRange={{
                    startDate: commonDate.fullDateStrToDate(
                      result.currentFilters.startDate,
                    ),
                    endDate: commonDate.fullDateStrToDate(
                      result.currentFilters.endDate,
                    ),
                  }}
                  sx={{
                    borderRadius: '6px',
                    w: '100%',
                  }}
                />
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl
                isInvalid={result.currentFilters.creditFacilityIds.length === 0}
              >
                <FormLabel>
                  <Heading fontSize="xl">Credit Facilities</Heading>
                  <Text color="gray">
                    Select the credit facilities to apply the filter.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={result.currentFilters.creditFacilityIds}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(creditFacilityIds) => {
                    result.setCurrentFilters({ creditFacilityIds });
                  }}
                  optionsHeader="Credit facilities"
                  options={facilities.map((facility) => ({
                    id: facility.id,
                    label: facility.name,
                    value: facility.id,
                  }))}
                  placeholderText="Select credit facilities"
                  showSelectedCount
                  titleText="All credit facilities"
                />
                <FormErrorMessage>
                  Select at least one facility.
                </FormErrorMessage>
              </FormControl>

              <PermissionGuard
                permissions={(context) =>
                  context.flags.productsLoansPermissions
                }
              >
                <Divider my="1.5rem" />
                <FormControl>
                  <FormLabel>
                    <Heading fontSize="xl">Loans</Heading>
                    <Text color="gray">
                      Select the loan(s) to apply the filter.
                    </Text>
                  </FormLabel>
                  <IsolatedMultiSelect
                    autoApplyOptions
                    buttonProps={dropdownButtonProps}
                    inputProps={inputProps}
                    isSearchable
                    initialSelections={result.currentFilters.loanIds.filter(
                      (loanId) =>
                        result.uniqueLoans.find((l) => l.id === loanId),
                    )}
                    matchWidthPopupBreakpoint={400}
                    menuItemOptionProps={menuItemOptionProps}
                    menuListProps={menuListProps}
                    onApplyOptions={(loanIds) => {
                      result.setCurrentFilters({ loanIds });
                    }}
                    options={result.uniqueLoans.map((loan) => ({
                      id: loan.id,
                      value: loan.id,
                      label: loan.id,
                    }))}
                    optionsHeader="Loans"
                    placeholderText="Select loans"
                    showSelectedCount
                    titleText="All loans"
                  />
                </FormControl>
              </PermissionGuard>

              <Divider my="1.5rem" />
              <FormControl
                isInvalid={result.currentFilters.accountTypes.length === 0}
              >
                <FormLabel>
                  <Heading fontSize="xl">Transaction Types</Heading>
                  <Text color="gray">
                    Choose the transaction type(s) to apply the filter.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={result.currentFilters.accountTypes}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(accountTypes) => {
                    result.setCurrentFilters({ accountTypes });
                  }}
                  options={result.uniqueTAccounts.map((accountType) => ({
                    id: accountType,
                    label: commonUtils.toTitleCase(accountType),
                    value: accountType,
                  }))}
                  optionsHeader="Transactions"
                  placeholderText="Select transaction types"
                  showSelectedCount
                  titleText="All transaction types"
                />
                <FormErrorMessage>
                  Select at least one transaction type.
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </DrawerBody>
          <Divider />
          <DrawerFooter justifyContent="space-between" m="1.5rem">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={() => result.setCurrentFilters(initialFilter)}
            >
              Reset Filters
            </Button>
            <Button
              isDisabled={
                result.currentFilters.creditFacilityIds.length === 0 ||
                result.currentFilters.accountTypes.length === 0
              }
              variant="primary"
              colorScheme="v2-aurora"
              onClick={() => {
                onFilterChange(result.currentFilters);
                drawerControls.onClose();
              }}
            >
              Apply Filters
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
